import React, { useState } from 'react';
import { SQL_WINDOW_CARDS } from './cards';
import './style.css';

const FAMILIES = ['All', 'Aggregate', 'Ranking', 'Offset', 'Value'];
const FACE_TITLES = ['Manager ticket', 'Business requirement', 'Function', 'OVER() solution'];
const FACE_HINTS = [
  'Think: what result does the manager actually need?',
  'Now choose the window function family and function.',
  'Finally decide PARTITION BY, ORDER BY and frame.',
  'Compare your solution with the model answer.',
];

export default function SqlWindowFlashcards() {
  const [family, setFamily] = useState('All');
  const [index, setIndex] = useState(0);
  const [face, setFace] = useState(0);
  const [mastered, setMastered] = useState({});
  const pool = SQL_WINDOW_CARDS.filter((card) => family === 'All' || card[0] === family);
  const card = pool[index];
  const masteredCount = Object.values(mastered).filter(Boolean).length;

  function changeFamily(event) {
    setFamily(event.target.value);
    setIndex(0);
    setFace(0);
  }

  function navigate(delta) {
    setIndex((current) => (current + delta + pool.length) % pool.length);
    setFace(0);
  }

  function toggleMastered() {
    setMastered((current) => ({ ...current, [card[1]]: !current[card[1]] }));
  }

  return (
    <section className="sql-window-flashcards" aria-label="SQL window function flashcards">
      <header className="flashcards-heading">
        <span className="eyebrow">DATA ENGINEERING • INTERACTIVE PRACTICE</span>
        <h2>SQL Window Function Cards</h2>
        <p>Read the manager ticket. Predict each answer before rotating to the next face.</p>
      </header>

      <div className="toolbar">
        <label>
          Function family
          <select value={family} onChange={changeFamily}>
            {FAMILIES.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
        <span>{index + 1} / {pool.length} tickets</span>
      </div>

      <div className="faces" role="group" aria-label="Card faces">
        {FACE_TITLES.map((title, faceIndex) => (
          <button
            key={title}
            type="button"
            className={faceIndex === face ? 'active' : ''}
            aria-pressed={faceIndex === face}
            onClick={() => setFace(faceIndex)}
          >
            <small>0{faceIndex + 1}</small>
            {title}
          </button>
        ))}
      </div>

      <div className="flashcard" aria-live="polite" aria-atomic="true">
        <div className="meta">
          <span>{card[0]} family</span>
          <span>Ticket {card[1]}</span>
        </div>
        <div className="content">
          <div className="face-tag">FACE {face + 1} OF 4</div>
          <h3>{FACE_TITLES[face]}</h3>
          {face === 0 && <blockquote>“{card[2]}”</blockquote>}
          {face === 1 && <p className="answer">{card[3]}</p>}
          {face === 2 && <p className="function">{card[4]}</p>}
          {face === 3 && <pre><code>{card[5]}</code></pre>}
        </div>
        <div className="hint">{FACE_HINTS[face]}</div>
      </div>

      <div className="actions">
        <button type="button" onClick={() => navigate(-1)}>← Previous</button>
        <button
          type="button"
          className="primary"
          onClick={() => face < 3 ? setFace(face + 1) : navigate(1)}
        >
          {face < 3 ? 'Flip to next face ↻' : 'Next ticket →'}
        </button>
        <button type="button" onClick={() => navigate(1)}>Skip →</button>
      </div>

      <div className="actions secondary">
        <button type="button" onClick={() => setFace(0)}>Reset card</button>
        <button type="button" aria-pressed={Boolean(mastered[card[1]])} onClick={toggleMastered}>
          {mastered[card[1]] ? '✓ Mastered' : 'Mark mastered'}
        </button>
        <span>{masteredCount} mastered / {SQL_WINDOW_CARDS.length}</span>
      </div>

      <footer>
        SQL Server / Azure Synapse T-SQL · Value family includes two SQL Server-compatible
        {' '}NTH_VALUE workarounds · Sample tickets assume correctly prepared source data.
      </footer>
    </section>
  );
}
